import {
  ICellValue,
  IMatrixSettings,
  ITableRow,
  ITableViewCell,
  ITableViewColumn,
  ITableViewRowLock,
  IUpdateTableViewCell
} from 'types'
import {
  IColumnTypes,
  IColumnValuesCount,
  ISpreadsheetData,
  IViewCoordinate,
  IViewCoordinateMap,
  IViewTruncation,
  ITransposedResult
} from 'components/spreadsheet/types'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { DEFAULT_MATRIX_COLUMN_WIDTH, GROUP_KEY_SPLIT } from 'components/spreadsheet/constants/const'
import { NO_VALUE_KEY, createGroupKey } from 'components/spreadsheet/helpers/grouping'
import { getRowLockState } from 'components/spreadsheet/helpers/locking'

/** Sentinel for pivot-column nulls. Reuses the grouping helper's
 *  ``NO_VALUE_KEY`` so the matrix and grouping code agree on what an
 *  "empty value" looks like internally. Kept distinct from a real empty
 *  string so a column with both ``null`` and ``""`` doesn't collapse two
 *  separate column-label buckets into one. */
const NULL_COL_LABEL_KEY = NO_VALUE_KEY
/** What an empty/null pivot value renders as in the column header — mirrors
 *  the transpose view's ``'(empty)'`` convention. */
const NULL_COL_LABEL_DISPLAY = '(empty)'

/**
 * Limits on the matrix the grid materialises: every data column is a column object and every
 * matrix row holds a value and a coordinate per column, so a high-cardinality column label (a
 * document number, say) over a large table ran the browser out of memory. Past these the matrix
 * shows the first column labels / row labels and says so (IMatrixComputeResult.truncations).
 */
export const MAX_MATRIX_DATA_COLUMNS = 2000
export const MAX_MATRIX_CELLS = 2_000_000

export interface IMatrixComputeResult extends ITransposedResult {
  // Sorted distinct values from the pivot column — useful for tests and for
  // rendering the column-group header strip.
  colLabelValues: string[]
  // Synthetic cell keys (``${matrixRowId}:${syntheticColumnId}``) for cells
  // where ``(row labels, column label)`` matched more than one source row.
  // Cells are left blank; the spreadsheet's standard validation indicator
  // surfaces the issue via this set.
  collisionCells: Set<string>
  // Synthetic cell keys for data cells with NO matching source row.
  sparseCells: Set<string>
  // Synthetic column public IDs that are structurally read-only regardless of
  // row-lock rules: row-label identifier columns, and data columns whose
  // source is formula/script/joined/synced (editing would fail server-side or
  // be overwritten on recompute). Rule-based locking is handled separately,
  // per-cell, by ``getMatrixLockedCells``.
  readOnlyColumns: Set<string>
}

const EMPTY_RESULT: IMatrixComputeResult = {
  rows: [],
  columns: [],
  columnIdToIndex: new Map(),
  indexToColumnId: [],
  coordinateMap: new Map(),
  truncations: [],
  colLabelValues: [],
  collisionCells: new Set(),
  sparseCells: new Set(),
  readOnlyColumns: new Set()
}

function getCellValue(
  row: ITableRow,
  columnIndex: number | undefined,
  columnStore: CompressedColumnStore | null
): ICellValue {
  if (columnIndex === undefined) return null
  if (row.rowData) return row.rowData[columnIndex] ?? null
  if (columnStore && row._rowIndex >= 0) return columnStore.get(row._rowIndex, columnIndex)
  return null
}

/** Display-formatting fields copied from a source column onto its synthetic
 *  matrix column so the matrix renders values identically to the base view
 *  (e.g. a ``decimal`` column keeps its 2-decimal-places formatting instead of
 *  falling back to the ``0`` default — #matrix-decimal). */
type SyntheticDisplayFormat = Pick<
  ITableViewColumn,
  'thousandSeparator' | 'displayLink' | 'decimalPlaces' | 'dateFormat'
>

function makeSyntheticColumn(
  publicId: string,
  name: string,
  sortOrder: number,
  width: number,
  kind: IColumnTypes,
  displayFormat?: SyntheticDisplayFormat
): ITableViewColumn {
  return {
    publicId,
    name,
    isIndexed: false,
    width,
    kind,
    description: null,
    script: null,
    scriptEnabled: false,
    formula: null,
    formulaEnabled: false,
    aggregate: 0,
    isJoined: false,
    thousandSeparator: displayFormat?.thousandSeparator ?? false,
    displayLink: displayFormat?.displayLink ?? false,
    decimalPlaces: displayFormat?.decimalPlaces ?? 0,
    dateFormat: displayFormat?.dateFormat ?? null,
    defaultValue: null,
    headerBackgroundColor: '',
    headerTextColor: '',
    exportWidth: null,
    isSynced: false,
    sortOrder,
    required: false,
    stringValidation: null,
    validationMessage: null,
    hardValidation: false,
    validationNoBlanks: false,
    validationNoDuplicates: false,
    displayValidationError: false,
    blockInvalidSelectValues: false,
    formWidth: null
  }
}

/** UTF-8 safe base64 encoder used to escape the column-label value segment
 *  of synthetic matrix column IDs. **/
function utf8ToBase64(value: string): string {
  if (typeof TextEncoder !== 'undefined' && typeof btoa === 'function') {
    const bytes = new TextEncoder().encode(value)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/=+$/, '')
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value, 'utf8').toString('base64').replace(/=+$/, '')
  }
  throw new Error('syntheticMatrixColumnId: no UTF-8 base64 encoder available')
}

export function syntheticMatrixColumnId(colLabelValue: string, dataColumnPublicId: string): string {
  // ``matrix::<b64(colLabelValue)>::<dataColumnPublicId>``. Base64 output is
  // [A-Za-z0-9+/] so the ``::`` separators stay parseable even when the
  // user's column-label value contains ``::``, spaces, or non-ASCII bytes.
  return `matrix::${utf8ToBase64(colLabelValue)}::${dataColumnPublicId}`
}

export function matrixRowKey(values: ICellValue[], kinds: IColumnTypes[]): string {
  return values.map((v, i) => (v == null ? NO_VALUE_KEY : createGroupKey(v, kinds[i]))).join(GROUP_KEY_SPLIT)
}

function naturalSortKey(value: string): Array<[number, string]> {
  const re = /\d+|\D+/g
  const chunks = value.match(re) ?? []
  return chunks.map((chunk) => {
    if (/^\d+$/.test(chunk)) return [parseInt(chunk, 10), ''] as [number, string]
    return [Number.POSITIVE_INFINITY, chunk.toLocaleLowerCase()] as [number, string]
  })
}

function compareNaturalKeys(a: Array<[number, string]>, b: Array<[number, string]>): number {
  const len = Math.min(a.length, b.length)
  for (let i = 0; i < len; i++) {
    const [an, as] = a[i]
    const [bn, bs] = b[i]
    if (an !== bn) return an - bn
    if (as !== bs) return as < bs ? -1 : 1
  }
  return a.length - b.length
}

/** Pure client-side pivot of ``spreadsheetData`` rows according to
 * ``matrixSettings``.
 *
 * Produces the derived matrix view used by the spreadsheet, including the
 * ``coordinateMap`` metadata that lets the matrix adapter map unambiguous
 * edits back to their source row/column. Cells with multiple matching source
 * rows or no backing source row remain non-remappable.
 */
/** How much of the matrix to materialise; the grid uses the defaults, an export turns the limits off. */
export interface MatrixLimits {
  maxDataColumns?: number
  maxCells?: number
}

export function getMatrixData(
  spreadsheetData: ISpreadsheetData,
  matrixSettings: IMatrixSettings | null,
  limits: MatrixLimits = {}
): IMatrixComputeResult {
  const maxDataColumns = limits.maxDataColumns ?? MAX_MATRIX_DATA_COLUMNS
  const maxCells = limits.maxCells ?? MAX_MATRIX_CELLS
  if (!matrixSettings || !matrixSettings.pivotColumnId) return EMPTY_RESULT
  if (matrixSettings.rowLabelColumnIds.length === 0) return EMPTY_RESULT
  if (matrixSettings.dataFieldColumnIds.length === 0) return EMPTY_RESULT

  const { columnIdToIndex, columnStore, rows, visibleBitmap, viewDetails } = spreadsheetData

  // Synthetic column IDs that are structurally read-only (see
  // ``IMatrixComputeResult.readOnlyColumns``).
  const readOnlyColumns = new Set<string>()

  // Pivot column is non-negotiable — without an axis the layout collapses.
  const pivotIndex = columnIdToIndex.get(matrixSettings.pivotColumnId)
  if (pivotIndex === undefined) return EMPTY_RESULT

  // Row labels & data fields tolerate missing columns by silently dropping
  // them (e.g. a column was deleted on another view but the matrix settings
  // weren't reconciled in time). Only bail out if NOTHING valid is left —
  // otherwise the matrix renders the configured-and-still-present subset.
  const rowLabelEntries = matrixSettings.rowLabelColumnIds
    .map((id) => ({ id, idx: columnIdToIndex.get(id), col: viewDetails.columns.find((c) => c.publicId === id) }))
    .filter((e): e is { id: string; idx: number; col: ITableViewColumn } => e.idx !== undefined && Boolean(e.col))
  const dataFieldEntries = matrixSettings.dataFieldColumnIds
    .map((id) => ({ id, idx: columnIdToIndex.get(id), col: viewDetails.columns.find((c) => c.publicId === id) }))
    .filter((e): e is { id: string; idx: number; col: ITableViewColumn } => e.idx !== undefined && Boolean(e.col))
  if (rowLabelEntries.length === 0) return EMPTY_RESULT
  if (dataFieldEntries.length === 0) return EMPTY_RESULT

  const rowLabelIndexes = rowLabelEntries.map((e) => e.idx)
  const dataIndexes = dataFieldEntries.map((e) => e.idx)
  const rowLabelColumns = rowLabelEntries.map((e) => e.col)
  const dataColumns = dataFieldEntries.map((e) => e.col)
  const rowLabelKinds = rowLabelColumns.map((c) => c.kind)

  // Two passes over the source rows. The first only keys each row (its row-label tuple and its
  // column label) and notes the distinct keys in first-seen order: that decides the matrix's shape
  // and how much of it fits the limits. The second collects source rows for the kept cells only, so
  // a million-row table with a million distinct labels never holds a bucket per source row.
  const visibleRowIndexes: number[] = []
  const rowKeys: string[] = []
  const colKeys: string[] = []
  const rowKeyFirstSeen = new Map<string, number>()
  const colKeyFirstSeen = new Map<string, number>()

  for (let i = 0; i < rows.length; i++) {
    if (visibleBitmap && !visibleBitmap[i]) continue
    const row = rows[i]
    const labelValues = rowLabelIndexes.map((idx) => getCellValue(row, idx, columnStore))
    const rowKey = matrixRowKey(labelValues, rowLabelKinds)
    const pivotValue = getCellValue(row, pivotIndex, columnStore)
    const colKey = pivotValue == null ? NULL_COL_LABEL_KEY : String(pivotValue)

    visibleRowIndexes.push(i)
    rowKeys.push(rowKey)
    colKeys.push(colKey)
    if (!rowKeyFirstSeen.has(rowKey)) rowKeyFirstSeen.set(rowKey, i)
    if (!colKeyFirstSeen.has(colKey)) colKeyFirstSeen.set(colKey, i)
  }

  const naturalKeyCache = new Map<string, Array<[number, string]>>()
  for (const value of colKeyFirstSeen.keys()) naturalKeyCache.set(value, naturalSortKey(value))
  const allColLabelValues = [...colKeyFirstSeen.keys()].sort((a, b) => {
    const cmp = compareNaturalKeys(naturalKeyCache.get(a)!, naturalKeyCache.get(b)!)
    if (cmp !== 0) return cmp
    return (colKeyFirstSeen.get(a) ?? 0) - (colKeyFirstSeen.get(b) ?? 0)
  })

  const truncations: IViewTruncation[] = []
  const maxColLabels = Math.max(1, Math.floor(maxDataColumns / dataColumns.length))
  let colLabelValues = allColLabelValues
  if (allColLabelValues.length > maxColLabels) {
    colLabelValues = allColLabelValues.slice(0, maxColLabels)
    truncations.push({
      shown: colLabelValues.length,
      total: allColLabelValues.length,
      message: `Showing the first ${colLabelValues.length.toLocaleString()} of ${allColLabelValues.length.toLocaleString()} column labels. Choose a column label with fewer distinct values, or filter the view, to see the rest.`
    })
  }

  // Synthetic columns: row-label columns first (so the matrix reads left-to-right
  // like the requirements doc), then col-label × data-field Cartesian product.
  const dataWidths = matrixSettings.dataColumnWidths ?? null
  const columns: ITableViewColumn[] = []
  const columnIdToIndexOut = new Map<string, number>()
  const indexToColumnId: string[] = []

  rowLabelColumns.forEach((col, index) => {
    // Row-label columns are read-only — they're identifiers for the matrix
    // row, not a 1-1 mapping to a source cell.
    const id = `matrix-row-label::${col.publicId}`
    const width = dataWidths?.[id] ?? col.width ?? DEFAULT_MATRIX_COLUMN_WIDTH
    const synthetic = makeSyntheticColumn(id, col.name, index, width, col.kind, col)
    readOnlyColumns.add(synthetic.publicId)
    columns.push(synthetic)
    columnIdToIndexOut.set(synthetic.publicId, index)
    indexToColumnId.push(synthetic.publicId)
  })

  const rowLabelOffset = rowLabelColumns.length
  colLabelValues.forEach((colValue, colIdx) => {
    dataColumns.forEach((dataCol, fieldIdx) => {
      const id = syntheticMatrixColumnId(colValue, dataCol.publicId)
      const position = rowLabelOffset + colIdx * dataColumns.length + fieldIdx
      const width = dataWidths?.[id] ?? DEFAULT_MATRIX_COLUMN_WIDTH
      const displayColValue = colValue === NULL_COL_LABEL_KEY ? NULL_COL_LABEL_DISPLAY : colValue
      const header = displayColValue ? `${displayColValue} ${dataCol.name}` : dataCol.name
      const synthetic = makeSyntheticColumn(id, header, position, width, dataCol.kind, dataCol)
      // Synthetic columns drop their source's computed-ness, so the Row
      // component's intrinsic checks (formula/script/joined/synced) can't see
      // it — flag it here so the matrix renders it read-only.
      if (dataCol.formulaEnabled || dataCol.scriptEnabled || dataCol.isJoined || dataCol.isSynced)
        readOnlyColumns.add(id)
      columns.push(synthetic)
      columnIdToIndexOut.set(id, position)
      indexToColumnId.push(id)
    })
  })

  // Materialise matrix rows — as many as fit in MAX_MATRIX_CELLS cells, in first-seen order.
  const maxRows = Math.max(1, Math.floor(maxCells / Math.max(1, columns.length)))
  const allRowKeyCount = rowKeyFirstSeen.size
  const rowKeyOrder: string[] = []
  const keptRowKeys = new Set<string>()
  for (const rowKey of rowKeyFirstSeen.keys()) {
    if (rowKeyOrder.length >= maxRows) break
    rowKeyOrder.push(rowKey)
    keptRowKeys.add(rowKey)
  }
  if (allRowKeyCount > maxRows) {
    truncations.push({
      shown: maxRows,
      total: allRowKeyCount,
      message: `Showing the first ${maxRows.toLocaleString()} of ${allRowKeyCount.toLocaleString()} row labels. Filter the view to see the rest.`
    })
  }
  const keptColKeys = new Set(colLabelValues)

  // Second pass: the source rows behind each kept cell, by row key then column label, the label
  // values of each kept row, and how many source rows share each kept row key (all of them, in any
  // column, for the row-label consistency check below).
  const buckets = new Map<string, Map<string, ITableRow[]>>()
  const rowKeyToValues = new Map<string, ICellValue[]>()
  const rowKeyCount = new Map<string, number>()
  for (let k = 0; k < visibleRowIndexes.length; k++) {
    const rowKey = rowKeys[k]
    if (!keptRowKeys.has(rowKey)) continue
    const row = rows[visibleRowIndexes[k]]
    rowKeyCount.set(rowKey, (rowKeyCount.get(rowKey) ?? 0) + 1)
    if (!rowKeyToValues.has(rowKey)) {
      rowKeyToValues.set(
        rowKey,
        rowLabelIndexes.map((idx) => getCellValue(row, idx, columnStore))
      )
    }
    const colKey = colKeys[k]
    if (!keptColKeys.has(colKey)) continue
    let rowBuckets = buckets.get(rowKey)
    if (!rowBuckets) {
      rowBuckets = new Map()
      buckets.set(rowKey, rowBuckets)
    }
    const existing = rowBuckets.get(colKey)
    if (existing) existing.push(row)
    else rowBuckets.set(colKey, [row])
  }

  const materialisedRows: ITableRow[] = []
  const coordinateMap = new Map<string, IViewCoordinate>()
  const collisionCells = new Set<string>()
  const sparseCells = new Set<string>()

  rowKeyOrder.forEach((rowKey, rowIdx) => {
    const labelValues = rowKeyToValues.get(rowKey) ?? []
    const matrixRowId = `matrix-row::${rowIdx}::${rowKey.length}`
    const rowData: ICellValue[] = new Array(columns.length).fill(null)
    rowLabelColumns.forEach((col, i) => {
      rowData[i] = labelValues[i] ?? null
      coordinateMap.set(`${matrixRowId}:${columns[i].publicId}`, {
        originalRowId: null,
        originalColumnId: col.publicId
      })
    })

    colLabelValues.forEach((colValue, colIdx) => {
      const bucket = buckets.get(rowKey)?.get(colValue) ?? []
      dataColumns.forEach((dataCol, fieldIdx) => {
        const position = rowLabelOffset + colIdx * dataColumns.length + fieldIdx
        const synthId = columns[position].publicId
        if (bucket.length === 1) {
          // 1-1 mapping → cell is editable; writes route via coordinateMap.
          const sourceRow = bucket[0]
          const dataIdx = dataIndexes[fieldIdx]
          rowData[position] = getCellValue(sourceRow, dataIdx, columnStore)
          coordinateMap.set(`${matrixRowId}:${synthId}`, {
            originalRowId: sourceRow.publicId,
            originalColumnId: dataCol.publicId
          })
          return
        }
        // Sparse (0 source rows) or ambiguous (>1 source row) — leave the cell
        // blank with no source mapping. Ambiguous cells go into
        // ``collisionCells`` so the validation indicator paints them; sparse
        // cells go into ``sparseCells`` so the Row component renders them
        // locked
        rowData[position] = null
        coordinateMap.set(`${matrixRowId}:${synthId}`, {
          originalRowId: null,
          originalColumnId: dataCol.publicId
        })
        if (bucket.length > 1) {
          collisionCells.add(`${matrixRowId}:${synthId}`)
        } else {
          sparseCells.add(`${matrixRowId}:${synthId}`)
        }
      })
    })

    materialisedRows.push({
      publicId: matrixRowId,
      sortOrder: rowIdx,
      rowData,
      _rowIndex: -1
    })
  })

  // Row-label inconsistency detection.
  //
  // Each materialised matrix row is one unique (row-label-0, …, row-label-N)
  // tuple. If a user treats row-label-0 (e.g. ``Document``) as the primary
  // identifier and row-label-1 (e.g. ``Description``) as a descriptor that
  // *should* be 1-1 with the primary, the same primary appearing with
  // multiple distinct secondary values means an outlier.
  //
  // Only the *minority* value (the one with fewer underlying source rows) is
  // flagged — the dominant value is treated as the "correct" answer and left
  // alone. Ties (no clear winner) are left unflagged to avoid noise.
  for (let secondaryIdx = 1; secondaryIdx < rowLabelColumns.length; secondaryIdx++) {
    // primaryKey → max source-row count across its matrix rows
    const primaryMaxCount = new Map<string, number>()
    const rowKeyByMatrixRow = new Map<string, string>()
    for (let i = 0; i < materialisedRows.length; i++) {
      const matrixRow = materialisedRows[i]
      const rowKey = rowKeyOrder[i]
      rowKeyByMatrixRow.set(matrixRow.publicId, rowKey)
      const primaryKey = JSON.stringify(matrixRow.rowData?.slice(0, secondaryIdx) ?? [])
      const count = rowKeyCount.get(rowKey) ?? 0
      primaryMaxCount.set(primaryKey, Math.max(primaryMaxCount.get(primaryKey) ?? 0, count))
    }
    for (const matrixRow of materialisedRows) {
      const primaryKey = JSON.stringify(matrixRow.rowData?.slice(0, secondaryIdx) ?? [])
      const max = primaryMaxCount.get(primaryKey) ?? 0
      const rowKey = rowKeyByMatrixRow.get(matrixRow.publicId)!
      const count = rowKeyCount.get(rowKey) ?? 0
      // Strictly less than max ⇒ minority value ⇒ flag.
      if (count < max) {
        const colId = columns[secondaryIdx].publicId
        collisionCells.add(`${matrixRow.publicId}:${colId}`)
      }
    }
  }

  return {
    rows: materialisedRows,
    columns,
    columnIdToIndex: columnIdToIndexOut,
    indexToColumnId,
    coordinateMap,
    truncations,
    colLabelValues,
    collisionCells,
    sparseCells,
    readOnlyColumns
  }
}

/**
 * Compute which cells of a matrix row are locked by row-lock rules.
 *
 * Matrix rows are synthetic — ``matrixRow.rowData`` is indexed by synthetic
 * matrix column IDs, so a per-row evaluation against the matrix row itself
 * never matches a rule (which references source-table column IDs). Each
 * viewport cell, however, maps back via ``coordinateMap`` to a single source
 * (row, column) pair — and *that's* what the rule predicates expect.
 *
 * Returns the set of ``${matrixRowId}:${viewportColumnId}`` cell keys whose
 * underlying source row matches an ``applyToEntireRow=true`` rule, OR whose
 * source column matches a cell-only rule on that same source row.
 *
 * Different cells in a single matrix row can map to different source rows
 * (one source row per unique row-label × column-label × data-field cell), so
 * the per-source-row evaluation is cached internally to avoid recomputing
 * the same rule pass for cells that share a source row.
 */
export function getMatrixLockedCells(args: {
  matrixRow: ITableRow
  viewportColumnIds: string[]
  rowLockSettings: ITableViewRowLock[]
  coordinateMap: IViewCoordinateMap
  origColumns: ITableViewColumn[]
  origRows: ITableRow[]
  origColumnIdToIndex: Map<string, number>
  origColumnStore: CompressedColumnStore | null
  columnValuesCount: IColumnValuesCount
}): Set<string> {
  const {
    matrixRow,
    viewportColumnIds,
    rowLockSettings,
    coordinateMap,
    origColumns,
    origRows,
    origColumnIdToIndex,
    origColumnStore,
    columnValuesCount
  } = args

  const locked = new Set<string>()
  if (rowLockSettings.length === 0) return locked

  const origColumnLookup = new Map(origColumns.map((c) => [c.publicId, c]))
  const origRowMap = new Map(origRows.map((r) => [r.publicId, r]))
  const stateBySourceRowId = new Map<string, ReturnType<typeof getRowLockState>>()

  for (const viewportColumnId of viewportColumnIds) {
    const key = `${matrixRow.publicId}:${viewportColumnId}`
    const coord = coordinateMap.get(key)
    if (!coord?.originalRowId) continue

    let state = stateBySourceRowId.get(coord.originalRowId)
    if (state === undefined) {
      const origRow = origRowMap.get(coord.originalRowId)
      if (!origRow) {
        stateBySourceRowId.set(coord.originalRowId, { rowLocked: false, lockedColumnIds: new Set() })
        continue
      }
      state = getRowLockState(
        rowLockSettings,
        origRow,
        origColumnLookup,
        origColumnIdToIndex,
        columnValuesCount,
        origColumnStore
      )
      stateBySourceRowId.set(coord.originalRowId, state)
    }

    if (state.rowLocked) {
      locked.add(key)
      continue
    }
    if (coord.originalColumnId && state.lockedColumnIds.has(coord.originalColumnId)) {
      locked.add(key)
    }
  }

  return locked
}

/**
 * Map matrix-view cell edits back to their source (rowId, columnId) pair.
 *
 * Returns a remapped ``IUpdateTableViewCell`` or ``null`` if nothing maps.
 * Row-label synthetic cells are skipped (they have no source row), and so
 * are conflict / sparse cells where ``coordinateMap`` records ``originalRowId
 * = null`` (no unambiguous target — for sparse cells we'd need a row-create
 * flow, which is out of MVP scope).
 */
export function mapMatrixCells(
  cells: IUpdateTableViewCell,
  coordinateMap: IViewCoordinateMap
): IUpdateTableViewCell | null {
  const mapped: ITableViewCell[] = []
  for (const cell of cells.cells) {
    const coord = coordinateMap.get(`${cell.rowId}:${cell.columnId}`)
    if (coord && coord.originalRowId && coord.originalColumnId) {
      mapped.push({
        rowId: coord.originalRowId,
        columnId: coord.originalColumnId,
        value: cell.value
      })
    }
  }
  if (mapped.length === 0) return null
  return { cells: mapped, context: cells.context }
}
